import {
  Calculate as CalculateIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import RiskCalculatorSkeleton from 'lib/common/Skeleton/RiskCalculator';
import riskPremiumCalculator, {
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { Output } from 'lib/functions/riskPremiumCalculator';
import { formatMoney, showAlert } from 'lib/functions/utils';
import { SubProductCreditLineConcentration } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import RatesCollapse from './RatesCollapse';

interface DetailedRiskPremiumCalculation {
  amount: number;
  startDate?: string;
  endDate?: string;
  quotedCurrency: string;
  type: string;
  businessIdentifier: string | null;
  rateControls: RiskPremiumRates;
  setRateControls: React.Dispatch<React.SetStateAction<RiskPremiumRates>>;
  useConcentrationRates: boolean;
  canEditRates: boolean;
  isCreating?: boolean;
}

type SubProduct = SubProductCreditLineConcentration;
type CalculationResult = Output;

const useCreditLineData = (businessIdentifier: string | null) => {
  const dispatch = useDispatch();
  const [subProductsData, setSubProductsData] = useState<SubProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditLineData = async () => {
    try {
      setIsLoading(true);
      if (!businessIdentifier) return;
      const creditLine = await getCreditLineByProduct(
        dispatch,
        businessIdentifier,
        'sgr',
      );
      const data = creditLine?.subProducts || creditLine || [];
      setSubProductsData(data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };
  return {
    subProductsData,
    isLoading,
    error,
    fetchCreditLineData,
  };
};

const DetailedRiskPremiumCalculator: React.FC<
  DetailedRiskPremiumCalculation
> = ({
  amount,
  startDate,
  endDate,
  quotedCurrency,
  type,
  businessIdentifier,
  rateControls,
  setRateControls,
  useConcentrationRates,
  canEditRates = false,
  isCreating = false,
}) => {
  const dispatch = useDispatch();
  const { calculate } = riskPremiumCalculator();
  const { subProductsData, isLoading, fetchCreditLineData } =
    useCreditLineData(businessIdentifier);
  const [showRateControls, setShowRateControls] = useState(false);
  const [calculationResult, setCalculationResult] =
    useState<CalculationResult | null>(null);

  useEffect(() => {
    void fetchCreditLineData();
  }, [businessIdentifier]);

  useEffect(() => {
    if (!useConcentrationRates) return;
    if (subProductsData.length > 0 && type) {
      const subProduct = subProductsData.find(
        (sp: SubProduct) => sp.subProductId === type,
      );
      setRateControls((prev) => ({
        ...prev,
        variableRate: subProduct?.operationCostVariable ?? undefined,
        fixedRate: subProduct?.operationCostFixed ?? undefined,
        variableDiscount: subProduct?.discountRateVariable ?? undefined,
        fixedDiscount: subProduct?.discountRateFixed ?? undefined,
      }));
    }
  }, [subProductsData, type, setRateControls, useConcentrationRates]);

  useEffect(() => {
    const performCalculation = () => {
      try {
        const calculationBreakdown = calculate({
          quotedAmount: amount,
          quotedCurrency,
          startDate: startDate,
          endDate: endDate,
          rates: rateControls,
        });
        setCalculationResult(calculationBreakdown);
      } catch (error) {
        if (!isCreating) {
          console.error(error);
          showAlert(dispatch, 'error', 'Error al calcular el costo de emisión');
          setCalculationResult(null);
        }
      }
    };

    performCalculation();
  }, [amount, startDate, endDate, quotedCurrency, rateControls]);

  const handleRateChange = (field: keyof RiskPremiumRates, value: number) => {
    setRateControls((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (isLoading) return <RiskCalculatorSkeleton />;

  const totalPremium = calculationResult?.amount || 0;

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <CalculateIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Cálculo Detallado
          </Typography>
          <Chip
            label="SGR"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>
        {canEditRates && (
          <IconButton
            size="small"
            onClick={() => setShowRateControls(!showRateControls)}
            sx={{ color: 'primary.main' }}
          >
            <SettingsIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        )}
        {totalPremium > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalPremium)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>

      <RatesCollapse
        show={showRateControls}
        rateControls={rateControls}
        onRateChange={handleRateChange}
        subProductsData={subProductsData}
        type={type}
      />

      <Box sx={{ textAlign: 'center', my: 2 }}>
        {(!amount || !startDate || !endDate || !quotedCurrency || !type) ?? (
          <Box>
            <WarningIcon
              sx={{ fontSize: '2rem', color: 'text.disabled', mb: 0.75 }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                lineHeight: 1.2,
              }}
            >
              Complete todos los campos requeridos
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
            >
              Monto, días, tipo de garantía y tasa de conversión
            </Typography>
          </Box>
        )}
      </Box>

      {/* Calculation Breakdown */}
      {calculationResult && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              backgroundColor: 'grey.50',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Monto en CLP
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationResult.amountInCLP)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Variable Original
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.originalVariableRate.toFixed(2)}%
                  </Typography>
                </Stack>
              </Grid>
              {calculationResult.variableDiscount > 0 && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descuento Variable
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      -{calculationResult.variableDiscount.toFixed(2)}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Variable Efectiva
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.effectiveVariableRate.toFixed(2)}%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Componente Variable
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationResult.variableComponent)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Fija Original
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.originalFixedRate.toFixed(2)} UF
                  </Typography>
                </Stack>
              </Grid>
              {calculationResult.fixedDiscount > 0 && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descuento Fijo
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      -{calculationResult.fixedDiscount.toFixed(2)}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Fija Efectiva
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.effectiveFixedRate.toFixed(2)} UF
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Componente Fijo
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationResult.fixedComponent)} CLP
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
      {canEditRates && (
        <Alert
          severity="info"
          icon={<InfoIcon sx={{ fontSize: '0.9rem' }} />}
          sx={{
            borderRadius: 0.5,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            border: '1px solid rgba(25, 118, 210, 0.2)',
            py: 0.75,
            '& .MuiAlert-message': { py: 0.25 },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
            Información Importante
          </AlertTitle>
          <Typography
            variant="body2"
            sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
          >
            Las tasas mostradas pueden ser modificadas usando el botón de
            configuración.
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default DetailedRiskPremiumCalculator;
